import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik } from 'formik';
import NameBlock, { NameBlockSchema } from '~components/forms/blocks/NameBlock';
import { ValidationErrors } from '~components/forms/SubmissionHelpers';
import AddressBlock, {
  AddressBlockSchema,
  AddressBlockSchemaStateOnly,
} from '~components/forms/blocks/AddressBlock';
import DateOfBirthBlock, {
  DateOfBirthBlockSchema,
} from '~components/forms/blocks/DateOfBirthBlock';
import ContactInformationBlock, {
  ContactInformationBlockSchema,
  ContactInformationBlockSchemaPhoneRequired,
  ContactInformationBlockCollegeEditionSchema,
} from '~components/forms/blocks/ContactInformationBlock';
import ScrollIfValidationError from '~components/forms/ScrollIfValidationError';
import { CustomizableButton } from '~components/common/Button';
import { FormHelpText } from '~components/forms/HelpText';
import ReCAPTCHA from 'react-google-recaptcha';
import { RestorationDisclaimer } from '~components/forms/RestorationDisclaimer';
import { captchaEnabled, embedCaptchaEnabled } from '~utils/displayOptions';
import { useSelector } from 'react-redux';
import { SmsOptAndDisclaimer } from '~components/forms/OptIn';

function IntakeForm({
  formId,
  submitButtonId,
  onSubmit,
  formError,
  initialh1,
  helptext,
  isSubmitting,
  initialValues,
  isDateOfBirthRequired,
  isPhoneRequired,
  isAddress2Displayed,
  isAddressCollapsed,
  isStateOnly,
  isRestorationDisclaimerDisplayed,
  isEmbed,
  hasSubscriberOptIn,
}) {
  const sourcing = useSelector((state) => state.sourcing);

  let contactInformationSchema = ContactInformationBlockSchema;
  if (sourcing.edition === 'college') {
    contactInformationSchema = ContactInformationBlockCollegeEditionSchema;
  } else if (isPhoneRequired) {
    contactInformationSchema = ContactInformationBlockSchemaPhoneRequired;
  }

  const ValidationSchema = Yup.object().shape({
    ...NameBlockSchema,
    ...(isStateOnly ? AddressBlockSchemaStateOnly : AddressBlockSchema),
    ...(isDateOfBirthRequired && DateOfBirthBlockSchema),
    ...contactInformationSchema,
    sms_opt_in_subscriber: Yup.bool(),
    sms_opt_in_voteamerica: Yup.bool(),
  });

  const showCaptcha = isEmbed ? embedCaptchaEnabled : captchaEnabled;

  const [isCaptchaIncomplete, setCaptchaStatus] = useState(true);

  function onChange() {
    setCaptchaStatus(false);
  }
  const captchaClicked = onChange;

  let submitDisabled = false;
  if (showCaptcha && isCaptchaIncomplete) {
    submitDisabled = true;
  } else submitDisabled = !!isSubmitting;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={ValidationSchema}
      onSubmit={onSubmit}
      validateOnMount
      isEmbed={isEmbed}
    >
      {({ handleSubmit, isValid, submitCount, setFieldValue }) => (
        <form id={formId} onSubmit={handleSubmit} noValidate='novalidate'>
          <ScrollIfValidationError scrollToElement='h1' />
          <h1>{initialh1}</h1>
          {helptext && <FormHelpText helptext={helptext} />}
          {!isValid && submitCount > 0 ? (
            <ValidationErrors />
          ) : (
            formError.error && <ValidationErrors {...formError} />
          )}
          <div className='space-y-10'>
            <div className='space-y-5'>
              <NameBlock errors={formError.fieldErrors} required />
              <div className='divider' />

              <AddressBlock
                errors={formError.fieldErrors}
                isAddress2Displayed={isAddress2Displayed}
                isStateOnly={isStateOnly}
                isCollapsed={isAddressCollapsed}
                required
              />
              <div className='divider' />
              {isDateOfBirthRequired && (
                <>
                  <DateOfBirthBlock errors={formError.fieldErrors} required />
                  <div className='divider' />
                </>
              )}
              <ContactInformationBlock
                errors={formError.fieldErrors}
                isPhoneRequired={isPhoneRequired}
                setFieldValue={setFieldValue}
              />
              <SmsOptAndDisclaimer
                errors={formError.fieldErrors}
                hasSubscriberOptIn={hasSubscriberOptIn}
              />
            </div>

            {showCaptcha && (
              <ReCAPTCHA
                sitekey={process.env.GATSBY_GOOGLE_RECAPTCHA_SITE_KEY}
                onChange={captchaClicked}
              />
            )}

            <CustomizableButton
              type='submit'
              disabled={submitDisabled}
              id={submitButtonId}
              isSubmitting={isSubmitting}
              data-element-id='btn_x_continue'
              formSubmit
            >
              Continue
            </CustomizableButton>
            {isRestorationDisclaimerDisplayed && <RestorationDisclaimer />}
          </div>
        </form>
      )}
    </Formik>
  );
}

IntakeForm.propTypes = {
  formId: PropTypes.string.isRequired,
  submitButtonId: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialh1: PropTypes.string.isRequired,
  initialValues: PropTypes.object.isRequired,
  helptext: PropTypes.string,
  formError: PropTypes.object,
  isSubmitting: PropTypes.bool,
  isDateOfBirthRequired: PropTypes.bool,
  isPhoneRequired: PropTypes.bool,
  isAddress2Displayed: PropTypes.bool,
  isAddressCollapsed: PropTypes.bool,
  isStateOnly: PropTypes.bool,
  isRestorationDisclaimerDisplayed: PropTypes.bool,
  isCaptchaIncomplete: PropTypes.bool,
  isEmbed: PropTypes.bool,
  hasSubscriberOptIn: PropTypes.bool,
};

IntakeForm.defaultProps = {
  isSubmitting: false,
  isDateOfBirthRequired: true,
  isPhoneRequired: false,
  isAddress2Displayed: true,
  isAddressCollapsed: false,
  isStateOnly: false,
  isRestorationDisclaimerDisplayed: false,
  isCaptchaIncomplete: true,
  isEmbed: false,
  hasSubscriberOptIn: true,
};

export default IntakeForm;
